import type { Week, WeekState, WeekStatus } from './types.js';

/** Inside this window the UI switches to the urgent "FINAL HOURS" treatment. */
export const FINAL_HOURS_MS = 2 * 60 * 60 * 1000;

/** How long before close the status flips to "closing-soon". */
export const CLOSING_SOON_MS = 12 * 60 * 60 * 1000;

/**
 * Derives everything the UI needs about a ballot window from the stored dates
 * plus admin overrides. Clients re-derive this every second for countdowns, so
 * it must stay cheap and pure.
 */
export function deriveWeekState(week: Week, now: Date = new Date()): WeekState {
  const nowMs = now.getTime();
  const opensAt = Date.parse(week.ballotOpensAt);
  const closesAt = Date.parse(week.ballotClosesAt);
  const releaseAt = Date.parse(week.pollReleaseAt);

  const msUntilOpen = opensAt - nowMs;
  const msUntilClose = closesAt - nowMs;
  const msUntilRelease = releaseAt - nowMs;

  const manuallyClosed = week.votingManuallyClosed === true;
  const manuallyOpened = week.votingManuallyOpened === true;

  // Voting is closed once the deadline passes or an admin closes it early.
  // Calculating a preview poll must NOT close voting - an admin can inspect
  // running totals mid-week without ending the window.
  const votingClosed = manuallyClosed || (msUntilClose <= 0 && !manuallyOpened);

  let status: WeekStatus;
  if (week.pollReleasedAt) {
    status = 'released';
  } else if (votingClosed && week.pollCalculatedAt) {
    status = 'processing';
  } else if (votingClosed) {
    status = 'closed';
  } else if (manuallyOpened || msUntilOpen <= 0) {
    status = msUntilClose <= CLOSING_SOON_MS ? 'closing-soon' : 'open';
  } else {
    status = 'upcoming';
  }

  const canSubmitBallot = status === 'open' || status === 'closing-soon';

  return {
    week,
    status,
    msUntilOpen,
    msUntilClose,
    msUntilRelease,
    canSubmitBallot,
    isFinalHours: canSubmitBallot && msUntilClose > 0 && msUntilClose <= FINAL_HOURS_MS,
    pollReleased: status === 'released',
  };
}

export const WEEK_STATUS_LABEL: Record<WeekStatus, string> = {
  upcoming: 'Opens Soon',
  open: 'Ballot Open',
  'closing-soon': 'Closing Soon',
  closed: 'Voting Closed',
  processing: 'Results Processing',
  released: 'Poll Released',
};

export const WEEK_STATUS_TONE: Record<WeekStatus, 'neutral' | 'live' | 'urgent' | 'done'> = {
  upcoming: 'neutral',
  open: 'live',
  'closing-soon': 'urgent',
  closed: 'neutral',
  processing: 'neutral',
  released: 'done',
};

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
  expired: boolean;
}

export function countdownParts(targetMs: number): CountdownParts {
  const clamped = Math.max(0, targetMs);
  const totalSeconds = Math.floor(clamped / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    totalMs: clamped,
    expired: targetMs <= 0,
  };
}

/** "02:14:37" style clock used in the urgent deadline banner. */
export function formatCountdownClock(targetMs: number): string {
  const { days, hours, minutes, seconds } = countdownParts(targetMs);
  const totalHours = days * 24 + hours;
  return [totalHours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * Default ballot window for a given Saturday of games:
 *   opens  Saturday  7:00 AM ET
 *   closes Sunday    6:00 PM ET
 *   poll   Sunday    7:00 PM ET
 *
 * Stored as UTC instants; ET offset is -04:00 during the regular season
 * (daylight time) and -05:00 after the first Sunday in November.
 */
export function defaultWindowForSaturday(saturday: Date): {
  ballotOpensAt: string;
  ballotClosesAt: string;
  pollReleaseAt: string;
  gamesStart: string;
  gamesEnd: string;
} {
  const offsetHours = easternOffsetHours(saturday);
  const at = (dayOffset: number, hourEt: number) => {
    const date = new Date(saturday);
    date.setUTCDate(date.getUTCDate() + dayOffset);
    date.setUTCHours(hourEt + offsetHours, 0, 0, 0);
    return date.toISOString();
  };
  return {
    gamesStart: at(0, 11),
    gamesEnd: at(1, 1),
    ballotOpensAt: at(0, 7),
    ballotClosesAt: at(1, 18),
    pollReleaseAt: at(1, 19),
  };
}

/** US Eastern is UTC-4 on daylight time, UTC-5 on standard time. */
export function easternOffsetHours(date: Date): number {
  const year = date.getUTCFullYear();
  const dstStart = nthWeekdayOfMonth(year, 2, 0, 2); // second Sunday in March
  const dstEnd = nthWeekdayOfMonth(year, 10, 0, 1); // first Sunday in November
  const isDaylight = date >= dstStart && date < dstEnd;
  return isDaylight ? 4 : 5;
}

function nthWeekdayOfMonth(year: number, month: number, weekday: number, occurrence: number): Date {
  const first = new Date(Date.UTC(year, month, 1));
  const shift = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month, 1 + shift + (occurrence - 1) * 7, 7));
}
